import * as THREE from 'three';

/**
 * Sleek, transparent cockpit HUD for advanced starship simulation.
 */
export class PlayerShip {
  public readonly mesh: THREE.Group;
  private hud: THREE.Group;
  private leftPanel: THREE.Group;
  private rightPanel: THREE.Group;

  // Animated elements
  private scanRing!: THREE.Mesh;
  private targetingReticle!: THREE.Group;
  private speedIndicator!: THREE.Group;
  private attitudeIndicator!: THREE.Group;
  private speedometerNeedle!: THREE.Mesh;
  private speedBars: THREE.Mesh[] = [];
  private currentSpeed: number = 0;

  // Time warp indicator
  private timeWarpIndicator!: THREE.Group;
  private timeWarpBars: THREE.Mesh[] = [];
  private timeWarpPausedIndicator!: THREE.Mesh;

  // Weapon effects
  private muzzleFlash!: THREE.Group;
  private muzzleFlashIntensity: number = 0;

  // Thruster diagram
  private thrusterDiagram!: THREE.Group;
  private thrusters: Map<string, THREE.Mesh> = new Map();

  // Health/damage system
  private healthBar!: THREE.Group;
  private healthBarFill!: THREE.Mesh;
  private damageOverlay!: THREE.Mesh;

  constructor() {
    this.mesh = new THREE.Group();
    this.mesh.name = 'PlayerShip';

    // Create minimal HUD elements
    this.hud = this.createHUD();
    this.mesh.add(this.hud);

    // Create compact side panels
    this.leftPanel = this.createLeftPanel();
    this.mesh.add(this.leftPanel);

    this.rightPanel = this.createRightPanel();
    this.mesh.add(this.rightPanel);

    // 3D Attitude/Orientation indicator
    this.attitudeIndicator = this.createAttitudeIndicator();
    this.attitudeIndicator.position.set(0.85, 0.35, 0);
    this.mesh.add(this.attitudeIndicator);

    // Time warp indicator (top left)
    this.timeWarpIndicator = this.createTimeWarpIndicator();
    this.timeWarpIndicator.position.set(-0.85, 0.45, 0);
    this.mesh.add(this.timeWarpIndicator);

    // Muzzle flash effect
    this.muzzleFlash = this.createMuzzleFlash();
    this.mesh.add(this.muzzleFlash);

    // 3D Thruster diagram (bottom left, opposite to attitude indicator)
    this.thrusterDiagram = this.createThrusterDiagram();
    this.thrusterDiagram.position.set(-0.85, 0.35, 0);
    this.mesh.add(this.thrusterDiagram);

    // Player health bar (bottom center)
    this.healthBar = this.createHealthBar();
    this.healthBar.position.set(0, -0.55, 0);
    this.mesh.add(this.healthBar);

    // Damage overlay (full screen red flash)
    this.damageOverlay = this.createDamageOverlay();
    this.mesh.add(this.damageOverlay);

    // Thin cockpit frame edges (barely visible)
    this.createMinimalFrame();
  }

  private createHUD(): THREE.Group {
    const group = new THREE.Group();

    // Holographic cyan color - BOLDER
    const holoColor = 0x00ffff;
    const holoMat = new THREE.MeshBasicMaterial({
      color: holoColor,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
    });

    const holoMatBright = new THREE.MeshBasicMaterial({
      color: holoColor,
      transparent: true,
      opacity: 0.9,
    });

    // Center targeting reticle
    this.targetingReticle = new THREE.Group();

    // Outer rotating ring - THICKER
    this.scanRing = new THREE.Mesh(
      new THREE.RingGeometry(0.08, 0.09, 32),
      holoMat
    );
    this.targetingReticle.add(this.scanRing);

    // Second inner ring
    const innerRing = new THREE.Mesh(
      new THREE.RingGeometry(0.05, 0.055, 32),
      new THREE.MeshBasicMaterial({
        color: holoColor,
        transparent: true,
        opacity: 0.5,
      })
    );
    this.targetingReticle.add(innerRing);

    // Inner crosshair - THICKER lines
    const crossSize = 0.06;
    const crossGap = 0.025;
    const crossThickness = 0.006;
    const crossMat = holoMatBright.clone();

    // Top
    const crossTop = new THREE.Mesh(
      new THREE.PlaneGeometry(crossThickness, crossSize),
      crossMat
    );
    crossTop.position.y = crossGap + crossSize / 2;
    this.targetingReticle.add(crossTop);

    // Bottom
    const crossBottom = new THREE.Mesh(
      new THREE.PlaneGeometry(crossThickness, crossSize),
      crossMat
    );
    crossBottom.position.y = -(crossGap + crossSize / 2);
    this.targetingReticle.add(crossBottom);

    // Left
    const crossLeft = new THREE.Mesh(
      new THREE.PlaneGeometry(crossSize, crossThickness),
      crossMat
    );
    crossLeft.position.x = -(crossGap + crossSize / 2);
    this.targetingReticle.add(crossLeft);

    // Right
    const crossRight = new THREE.Mesh(
      new THREE.PlaneGeometry(crossSize, crossThickness),
      crossMat
    );
    crossRight.position.x = crossGap + crossSize / 2;
    this.targetingReticle.add(crossRight);

    // Center dot - BIGGER
    const centerDot = new THREE.Mesh(
      new THREE.CircleGeometry(0.008, 16),
      holoMatBright
    );
    this.targetingReticle.add(centerDot);

    group.add(this.targetingReticle);

    // Corner bracket frames
    this.addCornerBrackets(group);

    // Velocity vector indicator - BIGGER
    const velocityMarker = new THREE.Group();
    const vCircle = new THREE.Mesh(
      new THREE.RingGeometry(0.018, 0.024, 16),
      holoMat
    );
    velocityMarker.add(vCircle);

    // Wings on velocity marker - THICKER
    const vWingL = new THREE.Mesh(
      new THREE.PlaneGeometry(0.025, 0.004),
      holoMat
    );
    vWingL.position.x = -0.03;
    velocityMarker.add(vWingL);

    const vWingR = new THREE.Mesh(
      new THREE.PlaneGeometry(0.025, 0.004),
      holoMat
    );
    vWingR.position.x = 0.03;
    velocityMarker.add(vWingR);

    const vWingT = new THREE.Mesh(
      new THREE.PlaneGeometry(0.004, 0.025),
      holoMat
    );
    vWingT.position.y = 0.03;
    velocityMarker.add(vWingT);

    velocityMarker.position.set(0.06, 0.04, 0);
    group.add(velocityMarker);

    return group;
  }

  private addCornerBrackets(group: THREE.Group): void {
    const bracketMat = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.6,
    });

    const bracketLength = 0.12;
    const bracketThickness = 0.005;
    const bracketDist = 0.35;

    const corners = [
      { x: -bracketDist, y: bracketDist, dx: 1, dy: -1 },
      { x: bracketDist, y: bracketDist, dx: -1, dy: -1 },
      { x: -bracketDist, y: -bracketDist, dx: 1, dy: 1 },
      { x: bracketDist, y: -bracketDist, dx: -1, dy: 1 },
    ];

    corners.forEach((c) => {
      // Horizontal part
      const h = new THREE.Mesh(
        new THREE.PlaneGeometry(bracketLength, bracketThickness),
        bracketMat
      );
      h.position.set(c.x + (c.dx * bracketLength) / 2, c.y, 0);
      group.add(h);

      // Vertical part
      const v = new THREE.Mesh(
        new THREE.PlaneGeometry(bracketThickness, bracketLength),
        bracketMat
      );
      v.position.set(c.x, c.y + (c.dy * bracketLength) / 2, 0);
      group.add(v);
    });
  }

  private createCompassRing(): THREE.Mesh {
    // BOLDER compass ring
    const geometry = new THREE.RingGeometry(0.22, 0.235, 64);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.5,
    });

    const ring = new THREE.Mesh(geometry, material);

    // Add cardinal markers - THICKER
    const cardinalMat = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.8,
    });

    const markers = [0, 90, 180, 270];
    markers.forEach((angle) => {
      const rad = (angle * Math.PI) / 180;
      const marker = new THREE.Mesh(
        new THREE.PlaneGeometry(0.006, 0.03),
        cardinalMat
      );
      marker.position.x = Math.sin(rad) * 0.25;
      marker.position.y = Math.cos(rad) * 0.25;
      marker.rotation.z = -rad;
      ring.add(marker);
    });

    // Smaller tick marks - MORE VISIBLE
    for (let i = 0; i < 36; i++) {
      if (i % 9 === 0) continue; // Skip cardinals
      const rad = (i * 10 * Math.PI) / 180;
      const tick = new THREE.Mesh(
        new THREE.PlaneGeometry(0.003, 0.015),
        new THREE.MeshBasicMaterial({
          color: 0x00ffff,
          transparent: true,
          opacity: 0.4,
        })
      );
      tick.position.x = Math.sin(rad) * 0.24;
      tick.position.y = Math.cos(rad) * 0.24;
      tick.rotation.z = -rad;
      ring.add(tick);
    }

    // Heading indicator triangle at top
    const headingIndicator = new THREE.Mesh(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(0, 0.28, 0),
        new THREE.Vector3(-0.015, 0.26, 0),
        new THREE.Vector3(0.015, 0.26, 0),
      ]),
      new THREE.MeshBasicMaterial({ color: 0x00ffff, side: THREE.DoubleSide })
    );
    ring.add(headingIndicator);

    return ring;
  }

  private createAttitudeIndicator(): THREE.Group {
    const group = new THREE.Group();

    const holoColor = 0x00ffff;
    const holoMat = new THREE.MeshBasicMaterial({
      color: holoColor,
      transparent: true,
      opacity: 0.6,
    });

    // Background sphere (wireframe for 3D reference)
    const sphereGeo = new THREE.SphereGeometry(0.12, 16, 12);
    const sphereWire = new THREE.WireframeGeometry(sphereGeo);
    const sphereLines = new THREE.LineSegments(
      sphereWire,
      new THREE.LineBasicMaterial({
        color: holoColor,
        transparent: true,
        opacity: 0.15,
      })
    );
    group.add(sphereLines);

    // Outer ring (yaw/heading) - horizontal
    const outerRing = new THREE.Mesh(
      new THREE.TorusGeometry(0.12, 0.004, 8, 32),
      holoMat
    );
    outerRing.name = 'yawRing';
    group.add(outerRing);

    // Middle ring (pitch) - vertical, will rotate with pitch
    const middleRing = new THREE.Mesh(
      new THREE.TorusGeometry(0.1, 0.004, 8, 32),
      new THREE.MeshBasicMaterial({
        color: 0x00ff88,
        transparent: true,
        opacity: 0.6,
      })
    );
    middleRing.rotation.x = Math.PI / 2;
    middleRing.name = 'pitchRing';
    group.add(middleRing);

    // Inner ring (roll) - vertical perpendicular
    const innerRing = new THREE.Mesh(
      new THREE.TorusGeometry(0.08, 0.004, 8, 32),
      new THREE.MeshBasicMaterial({
        color: 0xff8800,
        transparent: true,
        opacity: 0.6,
      })
    );
    innerRing.rotation.y = Math.PI / 2;
    innerRing.name = 'rollRing';
    group.add(innerRing);

    // Ship direction indicator (small arrow/ship shape)
    const shipIndicator = new THREE.Group();
    shipIndicator.name = 'shipIndicator';

    // Ship body - forward pointing cone
    const noseCone = new THREE.Mesh(
      new THREE.ConeGeometry(0.015, 0.06, 8),
      new THREE.MeshBasicMaterial({ color: 0xffffff })
    );
    noseCone.rotation.x = -Math.PI / 2;
    noseCone.position.z = 0.03;
    shipIndicator.add(noseCone);

    // Ship body
    const body = new THREE.Mesh(
      new THREE.BoxGeometry(0.025, 0.012, 0.04),
      new THREE.MeshBasicMaterial({ color: 0xaaaaaa })
    );
    shipIndicator.add(body);

    // Wings
    const wingMat = new THREE.MeshBasicMaterial({ color: 0x888888 });
    const leftWing = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, 0.003, 0.025),
      wingMat
    );
    leftWing.position.set(-0.03, 0, -0.005);
    shipIndicator.add(leftWing);

    const rightWing = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, 0.003, 0.025),
      wingMat
    );
    rightWing.position.set(0.03, 0, -0.005);
    shipIndicator.add(rightWing);

    // Engine glow
    const engineGlow = new THREE.Mesh(
      new THREE.SphereGeometry(0.01, 8, 8),
      new THREE.MeshBasicMaterial({ color: 0x4488ff })
    );
    engineGlow.position.z = -0.025;
    shipIndicator.add(engineGlow);

    group.add(shipIndicator);

    // Axis lines
    // X axis (red) - right
    const xAxis = new THREE.Mesh(
      new THREE.CylinderGeometry(0.002, 0.002, 0.1, 8),
      new THREE.MeshBasicMaterial({ color: 0xff4444, transparent: true, opacity: 0.5 })
    );
    xAxis.rotation.z = Math.PI / 2;
    xAxis.position.x = 0.05;
    group.add(xAxis);

    // Y axis (green) - up
    const yAxis = new THREE.Mesh(
      new THREE.CylinderGeometry(0.002, 0.002, 0.1, 8),
      new THREE.MeshBasicMaterial({ color: 0x44ff44, transparent: true, opacity: 0.5 })
    );
    yAxis.position.y = 0.05;
    group.add(yAxis);

    // Z axis (blue) - forward
    const zAxis = new THREE.Mesh(
      new THREE.CylinderGeometry(0.002, 0.002, 0.1, 8),
      new THREE.MeshBasicMaterial({ color: 0x4444ff, transparent: true, opacity: 0.5 })
    );
    zAxis.rotation.x = Math.PI / 2;
    zAxis.position.z = 0.05;
    group.add(zAxis);

    // Label background
    const labelBg = new THREE.Mesh(
      new THREE.PlaneGeometry(0.1, 0.025),
      new THREE.MeshBasicMaterial({
        color: 0x001515,
        transparent: true,
        opacity: 0.5,
      })
    );
    labelBg.position.y = -0.16;
    group.add(labelBg);

    return group;
  }

  private createMuzzleFlash(): THREE.Group {
    const group = new THREE.Group();
    group.visible = false;

    // Central flash
    const flashGeo = new THREE.CircleGeometry(0.06, 16);
    const flashMat = new THREE.MeshBasicMaterial({
      color: 0xff6600,
      transparent: true,
      opacity: 0.9,
    });
    const flash = new THREE.Mesh(flashGeo, flashMat);
    group.add(flash);

    // Outer glow
    const glowGeo = new THREE.CircleGeometry(0.12, 16);
    const glowMat = new THREE.MeshBasicMaterial({
      color: 0xff4400,
      transparent: true,
      opacity: 0.5,
    });
    const glow = new THREE.Mesh(glowGeo, glowMat);
    group.add(glow);

    // Rays (starburst effect)
    const rayMat = new THREE.MeshBasicMaterial({
      color: 0xffaa00,
      transparent: true,
      opacity: 0.7,
    });

    for (let i = 0; i < 8; i++) {
      const ray = new THREE.Mesh(
        new THREE.PlaneGeometry(0.01, 0.15),
        rayMat
      );
      ray.rotation.z = (i / 8) * Math.PI * 2;
      ray.position.y = 0.05;
      ray.geometry.translate(0, 0.05, 0);
      group.add(ray);
    }

    // Position at bottom center (where guns would be)
    group.position.set(0, -0.4, -0.5);

    return group;
  }

  private createTimeWarpIndicator(): THREE.Group {
    const group = new THREE.Group();

    const holoColor = 0x00ffff;
    const lineMat = new THREE.MeshBasicMaterial({
      color: holoColor,
      transparent: true,
      opacity: 0.6,
    });

    // Panel background
    const panelBg = new THREE.Mesh(
      new THREE.PlaneGeometry(0.22, 0.1),
      new THREE.MeshBasicMaterial({
        color: 0x001515,
        transparent: true,
        opacity: 0.4,
      })
    );
    group.add(panelBg);

    // Panel border corners
    const cornerSize = 0.025;
    const hw = 0.11, hh = 0.05;
    const corners = [
      { x: -hw, y: hh, dx: 1, dy: -1 },
      { x: hw, y: hh, dx: -1, dy: -1 },
      { x: -hw, y: -hh, dx: 1, dy: 1 },
      { x: hw, y: -hh, dx: -1, dy: 1 },
    ];
    corners.forEach((c) => {
      const h = new THREE.Mesh(new THREE.PlaneGeometry(cornerSize, 0.002), lineMat);
      h.position.set(c.x + (c.dx * cornerSize) / 2, c.y, 0.001);
      group.add(h);
      const v = new THREE.Mesh(new THREE.PlaneGeometry(0.002, cornerSize), lineMat);
      v.position.set(c.x, c.y + (c.dy * cornerSize) / 2, 0.001);
      group.add(v);
    });

    // "TIME" label area
    const labelBg = new THREE.Mesh(
      new THREE.PlaneGeometry(0.06, 0.02),
      new THREE.MeshBasicMaterial({
        color: holoColor,
        transparent: true,
        opacity: 0.2,
      })
    );
    labelBg.position.set(-0.06, 0.025, 0.001);
    group.add(labelBg);

    // Clock icon (simple circle with hands)
    const clockRing = new THREE.Mesh(
      new THREE.RingGeometry(0.012, 0.015, 16),
      lineMat
    );
    clockRing.position.set(-0.085, 0.025, 0.002);
    group.add(clockRing);

    // Clock hands
    const hourHand = new THREE.Mesh(
      new THREE.PlaneGeometry(0.002, 0.008),
      new THREE.MeshBasicMaterial({ color: holoColor })
    );
    hourHand.position.set(-0.085, 0.028, 0.003);
    group.add(hourHand);

    const minHand = new THREE.Mesh(
      new THREE.PlaneGeometry(0.002, 0.01),
      new THREE.MeshBasicMaterial({ color: holoColor })
    );
    minHand.position.set(-0.082, 0.025, 0.003);
    minHand.rotation.z = -Math.PI / 2;
    group.add(minHand);

    // Time warp bars (like fast-forward symbols)
    this.timeWarpBars = [];
    for (let i = 0; i < 6; i++) {
      // Each bar is a small chevron shape (>>)
      const chevron = new THREE.Group();

      const line1 = new THREE.Mesh(
        new THREE.PlaneGeometry(0.003, 0.025),
        new THREE.MeshBasicMaterial({
          color: i < 3 ? 0x00ff88 : 0xffaa00,
          transparent: true,
          opacity: 0.2,
        })
      );
      line1.rotation.z = Math.PI / 4;
      line1.position.y = 0.006;
      chevron.add(line1);

      const line2 = new THREE.Mesh(
        new THREE.PlaneGeometry(0.003, 0.025),
        new THREE.MeshBasicMaterial({
          color: i < 3 ? 0x00ff88 : 0xffaa00,
          transparent: true,
          opacity: 0.2,
        })
      );
      line2.rotation.z = -Math.PI / 4;
      line2.position.y = -0.006;
      chevron.add(line2);

      chevron.position.set(-0.04 + i * 0.03, -0.02, 0.002);
      group.add(chevron);
      this.timeWarpBars.push(chevron as unknown as THREE.Mesh);
    }

    // Paused indicator (two vertical bars)
    const pauseGroup = new THREE.Group();
    pauseGroup.name = 'pauseIndicator';

    const pauseBar1 = new THREE.Mesh(
      new THREE.PlaneGeometry(0.008, 0.03),
      new THREE.MeshBasicMaterial({
        color: 0xff4444,
        transparent: true,
        opacity: 0.8,
      })
    );
    pauseBar1.position.x = -0.008;
    pauseGroup.add(pauseBar1);

    const pauseBar2 = new THREE.Mesh(
      new THREE.PlaneGeometry(0.008, 0.03),
      new THREE.MeshBasicMaterial({
        color: 0xff4444,
        transparent: true,
        opacity: 0.8,
      })
    );
    pauseBar2.position.x = 0.008;
    pauseGroup.add(pauseBar2);

    pauseGroup.position.set(0.07, -0.02, 0.002);
    pauseGroup.visible = false;
    this.timeWarpPausedIndicator = pauseGroup as unknown as THREE.Mesh;
    group.add(pauseGroup);

    return group;
  }

  private createHealthBar(): THREE.Group {
    const group = new THREE.Group();

    const holoColor = 0x00ffff;

    // Background panel
    const panelBg = new THREE.Mesh(
      new THREE.PlaneGeometry(0.5, 0.06),
      new THREE.MeshBasicMaterial({
        color: 0x001515,
        transparent: true,
        opacity: 0.5,
      })
    );
    group.add(panelBg);

    // Border corners
    const borderMat = new THREE.MeshBasicMaterial({
      color: holoColor,
      transparent: true,
      opacity: 0.5,
    });

    const corners = [
      { x: -0.25, y: 0.03, dx: 1, dy: -1 },
      { x: 0.25, y: 0.03, dx: -1, dy: -1 },
      { x: -0.25, y: -0.03, dx: 1, dy: 1 },
      { x: 0.25, y: -0.03, dx: -1, dy: 1 },
    ];
    corners.forEach((c) => {
      const h = new THREE.Mesh(new THREE.PlaneGeometry(0.03, 0.002), borderMat);
      h.position.set(c.x + (c.dx * 0.015), c.y, 0.001);
      group.add(h);
      const v = new THREE.Mesh(new THREE.PlaneGeometry(0.002, 0.02), borderMat);
      v.position.set(c.x, c.y + (c.dy * 0.01), 0.001);
      group.add(v);
    });

    // Health bar background
    const barBg = new THREE.Mesh(
      new THREE.PlaneGeometry(0.44, 0.035),
      new THREE.MeshBasicMaterial({
        color: 0x330000,
        transparent: true,
        opacity: 0.6,
      })
    );
    barBg.position.z = 0.001;
    group.add(barBg);

    // Health bar fill (green to yellow to red gradient simulated by color)
    this.healthBarFill = new THREE.Mesh(
      new THREE.PlaneGeometry(0.42, 0.025),
      new THREE.MeshBasicMaterial({
        color: 0x00ff44,
        transparent: true,
        opacity: 0.8,
      })
    );
    this.healthBarFill.position.z = 0.002;
    group.add(this.healthBarFill);

    // Health icon (heart shape simplified as diamond)
    const heartMat = new THREE.MeshBasicMaterial({
      color: 0xff4444,
      transparent: true,
      opacity: 0.8,
    });
    const heart = new THREE.Mesh(
      new THREE.CircleGeometry(0.012, 6),
      heartMat
    );
    heart.position.set(-0.28, 0, 0.003);
    group.add(heart);

    // "HULL" label area
    const labelBg = new THREE.Mesh(
      new THREE.PlaneGeometry(0.06, 0.018),
      new THREE.MeshBasicMaterial({
        color: holoColor,
        transparent: true,
        opacity: 0.2,
      })
    );
    labelBg.position.set(0.22, 0, 0.003);
    group.add(labelBg);

    return group;
  }

  private createDamageOverlay(): THREE.Mesh {
    // Full-screen red overlay for damage flash
    const overlay = new THREE.Mesh(
      new THREE.PlaneGeometry(4, 3),
      new THREE.MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
      })
    );
    overlay.position.z = -0.1; // Slightly in front of everything else
    overlay.name = 'damageOverlay';
    return overlay;
  }

  private createThrusterDiagram(): THREE.Group {
    const group = new THREE.Group();

    const holoColor = 0x00ffff;

    // Background panel
    const panelBg = new THREE.Mesh(
      new THREE.PlaneGeometry(0.28, 0.28),
      new THREE.MeshBasicMaterial({
        color: 0x001515,
        transparent: true,
        opacity: 0.4,
      })
    );
    group.add(panelBg);

    // Panel border
    const borderMat = new THREE.MeshBasicMaterial({
      color: holoColor,
      transparent: true,
      opacity: 0.5,
    });

    const cornerSize = 0.03;
    const hw = 0.14, hh = 0.14;
    const corners = [
      { x: -hw, y: hh, dx: 1, dy: -1 },
      { x: hw, y: hh, dx: -1, dy: -1 },
      { x: -hw, y: -hh, dx: 1, dy: 1 },
      { x: hw, y: -hh, dx: -1, dy: 1 },
    ];
    corners.forEach((c) => {
      const h = new THREE.Mesh(new THREE.PlaneGeometry(cornerSize, 0.002), borderMat);
      h.position.set(c.x + (c.dx * cornerSize) / 2, c.y, 0.001);
      group.add(h);
      const v = new THREE.Mesh(new THREE.PlaneGeometry(0.002, cornerSize), borderMat);
      v.position.set(c.x, c.y + (c.dy * cornerSize) / 2, 0.001);
      group.add(v);
    });

    // Create 3D ship model
    const shipGroup = new THREE.Group();
    shipGroup.name = 'shipModel';

    // Ship body (wedge shape)
    const bodyMat = new THREE.MeshBasicMaterial({ color: 0x888888 });
    const bodyGeo = new THREE.ConeGeometry(0.03, 0.08, 4);
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.rotation.x = -Math.PI / 2;
    body.rotation.z = Math.PI / 4;
    shipGroup.add(body);

    // Cockpit
    const cockpitMat = new THREE.MeshBasicMaterial({ color: 0x4488ff });
    const cockpit = new THREE.Mesh(
      new THREE.SphereGeometry(0.015, 8, 6),
      cockpitMat
    );
    cockpit.position.z = 0.03;
    cockpit.scale.set(1, 0.6, 1);
    shipGroup.add(cockpit);

    // Wings
    const wingMat = new THREE.MeshBasicMaterial({ color: 0x666666 });
    const wingGeo = new THREE.BoxGeometry(0.12, 0.005, 0.04);
    const wings = new THREE.Mesh(wingGeo, wingMat);
    wings.position.z = -0.01;
    shipGroup.add(wings);

    // Engine block
    const engineMat = new THREE.MeshBasicMaterial({ color: 0x444444 });
    const engineBlock = new THREE.Mesh(
      new THREE.BoxGeometry(0.04, 0.02, 0.03),
      engineMat
    );
    engineBlock.position.z = -0.04;
    shipGroup.add(engineBlock);

    group.add(shipGroup);

    // Create thruster flames (initially invisible)
    const thrusterMat = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0,
    });

    // Main engine (rear) - for forward thrust
    const mainThruster = new THREE.Mesh(
      new THREE.ConeGeometry(0.015, 0.05, 8),
      new THREE.MeshBasicMaterial({ color: 0x44aaff, transparent: true, opacity: 0 })
    );
    mainThruster.rotation.x = Math.PI / 2;
    mainThruster.position.z = -0.07;
    mainThruster.name = 'thruster_main';
    shipGroup.add(mainThruster);
    this.thrusters.set('forward', mainThruster);

    // Reverse thrusters (front) - for backward thrust
    const reverseThruster = new THREE.Mesh(
      new THREE.ConeGeometry(0.01, 0.03, 6),
      new THREE.MeshBasicMaterial({ color: 0xff6644, transparent: true, opacity: 0 })
    );
    reverseThruster.rotation.x = -Math.PI / 2;
    reverseThruster.position.z = 0.05;
    reverseThruster.name = 'thruster_reverse';
    shipGroup.add(reverseThruster);
    this.thrusters.set('backward', reverseThruster);

    // Left thruster - for right strafe
    const leftThruster = new THREE.Mesh(
      new THREE.ConeGeometry(0.008, 0.025, 6),
      new THREE.MeshBasicMaterial({ color: 0x44ff88, transparent: true, opacity: 0 })
    );
    leftThruster.rotation.z = -Math.PI / 2;
    leftThruster.position.set(-0.07, 0, 0);
    shipGroup.add(leftThruster);
    this.thrusters.set('right', leftThruster);

    // Right thruster - for left strafe
    const rightThruster = new THREE.Mesh(
      new THREE.ConeGeometry(0.008, 0.025, 6),
      new THREE.MeshBasicMaterial({ color: 0x44ff88, transparent: true, opacity: 0 })
    );
    rightThruster.rotation.z = Math.PI / 2;
    rightThruster.position.set(0.07, 0, 0);
    shipGroup.add(rightThruster);
    this.thrusters.set('left', rightThruster);

    // Bottom thruster - for up thrust
    const bottomThruster = new THREE.Mesh(
      new THREE.ConeGeometry(0.008, 0.025, 6),
      new THREE.MeshBasicMaterial({ color: 0xffff44, transparent: true, opacity: 0 })
    );
    bottomThruster.rotation.x = Math.PI;
    bottomThruster.position.set(0, -0.04, 0);
    shipGroup.add(bottomThruster);
    this.thrusters.set('up', bottomThruster);

    // Top thruster - for down thrust
    const topThruster = new THREE.Mesh(
      new THREE.ConeGeometry(0.008, 0.025, 6),
      new THREE.MeshBasicMaterial({ color: 0xffff44, transparent: true, opacity: 0 })
    );
    topThruster.rotation.x = 0;
    topThruster.position.set(0, 0.04, 0);
    shipGroup.add(topThruster);
    this.thrusters.set('down', topThruster);

    // Velocity arrow (shows direction of travel)
    const arrowGroup = new THREE.Group();
    arrowGroup.name = 'velocityArrow';

    const arrowShaft = new THREE.Mesh(
      new THREE.CylinderGeometry(0.003, 0.003, 0.08, 6),
      new THREE.MeshBasicMaterial({ color: 0x00ff00 })
    );
    arrowShaft.rotation.x = Math.PI / 2;
    arrowShaft.position.z = 0.04;
    arrowGroup.add(arrowShaft);

    const arrowHead = new THREE.Mesh(
      new THREE.ConeGeometry(0.008, 0.02, 6),
      new THREE.MeshBasicMaterial({ color: 0x00ff00 })
    );
    arrowHead.rotation.x = -Math.PI / 2;
    arrowHead.position.z = 0.09;
    arrowGroup.add(arrowHead);

    arrowGroup.visible = false; // Only show when moving
    group.add(arrowGroup);

    // Label
    const labelBg = new THREE.Mesh(
      new THREE.PlaneGeometry(0.08, 0.02),
      new THREE.MeshBasicMaterial({
        color: holoColor,
        transparent: true,
        opacity: 0.2,
      })
    );
    labelBg.position.y = -0.12;
    group.add(labelBg);

    return group;
  }

  private createLeftPanel(): THREE.Group {
    const group = new THREE.Group();
    group.position.set(-0.75, -0.35, 0);

    const holoColor = 0x00ffff;
    const lineMat = new THREE.MeshBasicMaterial({
      color: holoColor,
      transparent: true,
      opacity: 0.6,
    });

    // Panel background
    const panelBg = new THREE.Mesh(
      new THREE.PlaneGeometry(0.35, 0.28),
      new THREE.MeshBasicMaterial({
        color: 0x001515,
        transparent: true,
        opacity: 0.4,
      })
    );
    group.add(panelBg);

    // Panel border corners
    const cornerSize = 0.04;
    const hw = 0.17, hh = 0.13;
    const corners = [
      { x: -hw, y: hh, dx: 1, dy: -1 },
      { x: hw, y: hh, dx: -1, dy: -1 },
      { x: -hw, y: -hh, dx: 1, dy: 1 },
      { x: hw, y: -hh, dx: -1, dy: 1 },
    ];
    corners.forEach((c) => {
      const h = new THREE.Mesh(new THREE.PlaneGeometry(cornerSize, 0.003), lineMat);
      h.position.set(c.x + (c.dx * cornerSize) / 2, c.y, 0.001);
      group.add(h);
      const v = new THREE.Mesh(new THREE.PlaneGeometry(0.003, cornerSize), lineMat);
      v.position.set(c.x, c.y + (c.dy * cornerSize) / 2, 0.001);
      group.add(v);
    });

    // Speedometer arc
    const arcRadius = 0.1;
    const arcGeo = new THREE.RingGeometry(arcRadius - 0.008, arcRadius, 32, 1, Math.PI * 0.75, Math.PI * 1.5);
    const arc = new THREE.Mesh(arcGeo, lineMat);
    arc.position.y = -0.02;
    group.add(arc);

    // Speed tick marks
    for (let i = 0; i <= 10; i++) {
      const angle = Math.PI * 0.75 + (i / 10) * Math.PI * 1.5;
      const innerR = i % 5 === 0 ? arcRadius - 0.025 : arcRadius - 0.015;
      const outerR = arcRadius - 0.01;

      const tick = new THREE.Mesh(
        new THREE.PlaneGeometry(0.003, outerR - innerR),
        new THREE.MeshBasicMaterial({
          color: holoColor,
          transparent: true,
          opacity: i % 5 === 0 ? 0.8 : 0.4,
        })
      );
      const midR = (innerR + outerR) / 2;
      tick.position.set(
        Math.cos(angle) * midR,
        -0.02 + Math.sin(angle) * midR,
        0.001
      );
      tick.rotation.z = angle - Math.PI / 2;
      group.add(tick);
    }

    // Speedometer needle
    this.speedometerNeedle = new THREE.Mesh(
      new THREE.PlaneGeometry(0.006, 0.09),
      new THREE.MeshBasicMaterial({ color: 0xff4444 })
    );
    this.speedometerNeedle.position.set(0, -0.02, 0.002);
    this.speedometerNeedle.geometry.translate(0, 0.04, 0); // Pivot at bottom
    this.speedometerNeedle.rotation.z = Math.PI * 0.75; // Start position
    group.add(this.speedometerNeedle);

    // Center cap
    const centerCap = new THREE.Mesh(
      new THREE.CircleGeometry(0.015, 16),
      new THREE.MeshBasicMaterial({ color: 0x333333 })
    );
    centerCap.position.set(0, -0.02, 0.003);
    group.add(centerCap);

    // Speed value display area
    const valueBg = new THREE.Mesh(
      new THREE.PlaneGeometry(0.12, 0.04),
      new THREE.MeshBasicMaterial({
        color: 0x002222,
        transparent: true,
        opacity: 0.6,
      })
    );
    valueBg.position.set(0, 0.09, 0.001);
    group.add(valueBg);

    // Speed bars (digital display)
    this.speedBars = [];
    for (let i = 0; i < 12; i++) {
      const bar = new THREE.Mesh(
        new THREE.PlaneGeometry(0.006, 0.025),
        new THREE.MeshBasicMaterial({
          color: i < 8 ? 0x00ff88 : (i < 10 ? 0xffff00 : 0xff4444),
          transparent: true,
          opacity: 0.2,
        })
      );
      bar.position.set(-0.05 + i * 0.0085, 0.09, 0.002);
      group.add(bar);
      this.speedBars.push(bar);
    }

    // "M/S" label
    const labelBg = new THREE.Mesh(
      new THREE.PlaneGeometry(0.04, 0.015),
      new THREE.MeshBasicMaterial({
        color: holoColor,
        transparent: true,
        opacity: 0.3,
      })
    );
    labelBg.position.set(0, -0.12, 0.001);
    group.add(labelBg);

    // Keep speedIndicator reference for compatibility
    this.speedIndicator = group;

    return group;
  }

  private createRightPanel(): THREE.Group {
    const group = new THREE.Group();
    group.position.set(0.85, -0.4, 0);

    const lineMat = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.4,
    });

    // Subtle panel background
    const panelBg = new THREE.Mesh(
      new THREE.PlaneGeometry(0.25, 0.2),
      new THREE.MeshBasicMaterial({
        color: 0x001515,
        transparent: true,
        opacity: 0.3,
      })
    );
    group.add(panelBg);

    // Panel border corners
    const cornerSize = 0.03;
    const corners = [
      { x: -0.12, y: 0.09, ry: 1 },
      { x: 0.12, y: 0.09, ry: -1 },
      { x: -0.12, y: -0.09, ry: 1 },
      { x: 0.12, y: -0.09, ry: -1 },
    ];

    corners.forEach((c) => {
      const h = new THREE.Mesh(
        new THREE.PlaneGeometry(cornerSize, 0.002),
        lineMat
      );
      h.position.set(c.x + (c.ry * cornerSize) / 2, c.y, 0.001);
      group.add(h);

      const v = new THREE.Mesh(
        new THREE.PlaneGeometry(0.002, cornerSize),
        lineMat
      );
      v.position.set(c.x, c.y + (c.y > 0 ? -1 : 1) * cornerSize / 2, 0.001);
      group.add(v);
    });

    // Mini radar display
    const radarRing = new THREE.Mesh(
      new THREE.RingGeometry(0.06, 0.065, 32),
      lineMat
    );
    group.add(radarRing);

    // Radar grid lines
    const gridMat = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.15,
    });

    const hGrid = new THREE.Mesh(
      new THREE.PlaneGeometry(0.11, 0.001),
      gridMat
    );
    group.add(hGrid);

    const vGrid = new THREE.Mesh(
      new THREE.PlaneGeometry(0.001, 0.11),
      gridMat
    );
    group.add(vGrid);

    // Radar sweep (will be animated)
    const sweepShape = new THREE.Shape();
    sweepShape.moveTo(0, 0);
    sweepShape.lineTo(0.055, 0);
    sweepShape.absarc(0, 0, 0.055, 0, Math.PI / 8, false);
    sweepShape.lineTo(0, 0);

    const sweep = new THREE.Mesh(
      new THREE.ShapeGeometry(sweepShape),
      new THREE.MeshBasicMaterial({
        color: 0x00ff88,
        transparent: true,
        opacity: 0.3,
      })
    );
    sweep.name = 'radarSweep';
    group.add(sweep);

    return group;
  }

  private createMinimalFrame(): void {
    const frameMat = new THREE.MeshBasicMaterial({
      color: 0x222222,
      transparent: true,
      opacity: 0.4,
    });

    // Very thin edge lines - just hints of a cockpit frame
    const edgeThickness = 0.02;

    // Bottom edge
    const bottomEdge = new THREE.Mesh(
      new THREE.PlaneGeometry(2.5, edgeThickness),
      frameMat
    );
    bottomEdge.position.set(0, -0.7, 0);
    this.mesh.add(bottomEdge);

    // Subtle corner accents
    const accentMat = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.2,
    });

    // Bottom left accent
    const blAccent = new THREE.Mesh(
      new THREE.PlaneGeometry(0.15, 0.003),
      accentMat
    );
    blAccent.position.set(-1.1, -0.68, 0.001);
    this.mesh.add(blAccent);

    // Bottom right accent
    const brAccent = new THREE.Mesh(
      new THREE.PlaneGeometry(0.15, 0.003),
      accentMat
    );
    brAccent.position.set(1.1, -0.68, 0.001);
    this.mesh.add(brAccent);
  }

  public update(
    deltaTime: number,
    isMoving: boolean,
    cameraQuaternion?: THREE.Quaternion,
    speed: number = 0,
    timeScale: number = 1,
    isPaused: boolean = false,
    isFiring: boolean = false,
    thrustState?: { forward: boolean; backward: boolean; left: boolean; right: boolean; up: boolean; down: boolean },
    shipVelocity?: THREE.Vector3,
    healthPercent: number = 1,
    damageFlash: number = 0
  ): void {
    // Smooth speed value
    this.currentSpeed += (speed - this.currentSpeed) * deltaTime * 5;
    const time = Date.now() * 0.001;

    // Update time warp indicator
    this.updateTimeWarpIndicator(timeScale, isPaused, time);

    // Update muzzle flash
    this.updateMuzzleFlash(deltaTime, isFiring);

    // Update thruster diagram
    this.updateThrusterDiagram(thrustState, shipVelocity, cameraQuaternion);

    // Update health bar
    this.updateHealthBar(healthPercent);

    // Update damage overlay
    this.updateDamageOverlay(damageFlash);

    // Rotate targeting reticle outer ring slowly
    if (this.scanRing) {
      this.scanRing.rotation.z = time * 0.5;
    }

    // Pulse the targeting reticle
    if (this.targetingReticle) {
      const pulse = 1 + Math.sin(time * 3) * 0.05;
      this.targetingReticle.scale.set(pulse, pulse, 1);
    }

    // Animate radar sweep
    const sweep = this.rightPanel.getObjectByName('radarSweep') as THREE.Mesh;
    if (sweep) {
      sweep.rotation.z = -time * 2;
    }

    // Update 3D attitude indicator with camera orientation
    if (this.attitudeIndicator && cameraQuaternion) {
      const shipIndicator = this.attitudeIndicator.getObjectByName('shipIndicator');
      if (shipIndicator) {
        // Invert the quaternion so the indicator shows ship orientation relative to space
        shipIndicator.quaternion.copy(cameraQuaternion).invert();
      }

      // Rotate the gimbal rings to show orientation
      const yawRing = this.attitudeIndicator.getObjectByName('yawRing') as THREE.Mesh;
      const pitchRing = this.attitudeIndicator.getObjectByName('pitchRing') as THREE.Mesh;
      const rollRing = this.attitudeIndicator.getObjectByName('rollRing') as THREE.Mesh;

      if (yawRing && pitchRing && rollRing) {
        // Extract euler angles from quaternion
        const euler = new THREE.Euler().setFromQuaternion(cameraQuaternion, 'YXZ');

        // Apply rotations to rings (inverted to show reference frame)
        yawRing.rotation.y = -euler.y;
        pitchRing.rotation.z = euler.x;
        rollRing.rotation.x = -euler.z;
      }
    }

    // Update speedometer
    if (this.speedometerNeedle) {
      // Map speed to needle angle (0-1000 range maps to the arc)
      const maxSpeed = 500;
      const speedRatio = Math.min(this.currentSpeed / maxSpeed, 1);
      const needleAngle = Math.PI * 0.75 + speedRatio * Math.PI * 1.5;
      this.speedometerNeedle.rotation.z = needleAngle;
    }

    // Update speed bars
    if (this.speedBars.length > 0) {
      const maxSpeed = 500;
      const speedRatio = Math.min(this.currentSpeed / maxSpeed, 1);
      const activeBars = Math.floor(speedRatio * this.speedBars.length);

      this.speedBars.forEach((bar, i) => {
        const mat = bar.material as THREE.MeshBasicMaterial;
        if (i < activeBars) {
          mat.opacity = 0.8 + Math.sin(time * 8 + i) * 0.2;
        } else if (i === activeBars) {
          mat.opacity = 0.4 + (speedRatio * this.speedBars.length - activeBars) * 0.4;
        } else {
          mat.opacity = 0.15;
        }
      });
    }

    // Subtle movement shake
    if (isMoving) {
      this.mesh.position.x = Math.sin(time * 20) * 0.001;
      this.mesh.position.y = Math.sin(time * 17) * 0.001;
    } else {
      this.mesh.position.x = 0;
      this.mesh.position.y = 0;
    }
  }

  private updateThrusterDiagram(
    thrustState?: { forward: boolean; backward: boolean; left: boolean; right: boolean; up: boolean; down: boolean },
    shipVelocity?: THREE.Vector3,
    cameraQuaternion?: THREE.Quaternion
  ): void {
    if (!this.thrusterDiagram) return;

    const time = Date.now() * 0.001;

    // Update each thruster based on input
    const defaultState = { forward: false, backward: false, left: false, right: false, up: false, down: false };
    const state = thrustState || defaultState;

    // Animate thrusters
    this.thrusters.forEach((thruster, key) => {
      const isActive = state[key as keyof typeof state] || false;
      const mat = thruster.material as THREE.MeshBasicMaterial;

      if (isActive) {
        // Thruster is firing - show flame with flickering
        mat.opacity = 0.7 + Math.sin(time * 30) * 0.3;
        // Slight scale animation
        const flicker = 1 + Math.sin(time * 25) * 0.2;
        thruster.scale.set(flicker, 1, flicker);
      } else {
        // Thruster is off
        mat.opacity = 0;
      }
    });

    // Update velocity arrow to show direction of travel
    const velocityArrow = this.thrusterDiagram.getObjectByName('velocityArrow');
    if (velocityArrow && shipVelocity && cameraQuaternion) {
      const speed = shipVelocity.length();

      if (speed > 1) {
        velocityArrow.visible = true;

        // Get velocity direction in camera-local space
        const invQuat = cameraQuaternion.clone().invert();
        const localVelocity = shipVelocity.clone().applyQuaternion(invQuat).normalize();

        // Point the arrow in the velocity direction
        velocityArrow.lookAt(
          velocityArrow.position.x + localVelocity.x,
          velocityArrow.position.y + localVelocity.y,
          velocityArrow.position.z + localVelocity.z
        );

        // Scale based on speed
        const arrowScale = Math.min(speed / 100, 1.5);
        velocityArrow.scale.setScalar(arrowScale);
      } else {
        velocityArrow.visible = false;
      }
    }

    // Rotate the ship model slightly for 3D effect (like the attitude indicator)
    const shipModel = this.thrusterDiagram.getObjectByName('shipModel');
    if (shipModel && cameraQuaternion) {
      // Show ship orientation relative to world
      shipModel.quaternion.copy(cameraQuaternion).invert();
    }
  }

  private updateMuzzleFlash(deltaTime: number, isFiring: boolean): void {
    if (isFiring) {
      this.muzzleFlashIntensity = 1;
      this.muzzleFlash.visible = true;
    } else {
      this.muzzleFlashIntensity -= deltaTime * 15; // Fade out quickly
    }

    if (this.muzzleFlashIntensity <= 0) {
      this.muzzleFlashIntensity = 0;
      this.muzzleFlash.visible = false;
    } else {
      // Scale and fade the flash
      const scale = 0.5 + this.muzzleFlashIntensity * 0.5;
      this.muzzleFlash.scale.set(scale, scale, 1);

      // Random rotation for variety
      this.muzzleFlash.rotation.z = Math.random() * Math.PI * 2;

      // Update opacity
      this.muzzleFlash.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const mat = child.material as THREE.MeshBasicMaterial;
          mat.opacity = mat.opacity * this.muzzleFlashIntensity;
        }
      });
    }
  }

  private updateHealthBar(healthPercent: number): void {
    if (!this.healthBarFill) return;

    // Scale the health bar
    this.healthBarFill.scale.x = Math.max(0.01, healthPercent);
    this.healthBarFill.position.x = -(1 - healthPercent) * 0.21;

    // Change color based on health
    const mat = this.healthBarFill.material as THREE.MeshBasicMaterial;
    if (healthPercent > 0.6) {
      mat.color.setHex(0x00ff44); // Green
    } else if (healthPercent > 0.3) {
      mat.color.setHex(0xffff00); // Yellow
    } else {
      mat.color.setHex(0xff4444); // Red
      // Pulse when low health
      mat.opacity = 0.6 + Math.sin(Date.now() * 0.01) * 0.3;
    }
  }

  private updateDamageOverlay(damageFlash: number): void {
    if (!this.damageOverlay) return;

    const mat = this.damageOverlay.material as THREE.MeshBasicMaterial;
    mat.opacity = damageFlash * 0.5; // Max 50% opacity
  }

  private updateTimeWarpIndicator(timeScale: number, isPaused: boolean, time: number): void {
    if (!this.timeWarpBars || this.timeWarpBars.length === 0) return;

    // Show/hide pause indicator
    if (this.timeWarpPausedIndicator) {
      this.timeWarpPausedIndicator.visible = isPaused;
      // Blink when paused
      if (isPaused) {
        const blinkOn = Math.sin(time * 4) > 0;
        this.timeWarpPausedIndicator.visible = blinkOn;
      }
    }

    // Calculate how many bars to light up based on time scale
    // Scale mapping: 0.125 = 1 bar, 0.25 = 1, 0.5 = 2, 1 = 3, 2 = 4, 4 = 5, 8+ = 6
    let activeBars = 3; // Default for 1x
    if (timeScale <= 0.125) activeBars = 0;
    else if (timeScale <= 0.25) activeBars = 1;
    else if (timeScale <= 0.5) activeBars = 2;
    else if (timeScale <= 1) activeBars = 3;
    else if (timeScale <= 2) activeBars = 4;
    else if (timeScale <= 4) activeBars = 5;
    else activeBars = 6;

    // Update bar visibility and animation
    this.timeWarpBars.forEach((chevron, i) => {
      const isActive = i < activeBars;
      chevron.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const mat = child.material as THREE.MeshBasicMaterial;
          if (isActive) {
            // Active bars glow and pulse when time is fast
            const pulseIntensity = timeScale > 1 ? 0.3 : 0.1;
            mat.opacity = 0.7 + Math.sin(time * 5 + i * 0.5) * pulseIntensity;
          } else {
            mat.opacity = 0.15;
          }
        }
      });
    });
  }

  public setVisible(visible: boolean): void {
    this.mesh.visible = visible;
  }

  public dispose(): void {
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
}
